import React, { useState, useEffect } from 'react';
import { FaTimes, FaCheck, FaTimesCircle, FaUsers, FaTrophy, FaEnvelope } from 'react-icons/fa';
import styles from './TeamInvitationResponseModal.module.css';
import { teamInvitationApi, INVITATION_RESPONSE } from '../../api/teamInvitations';

const TeamInvitationResponseModal = ({ 
  invitation, 
  onClose, 
  onResponse 
}) => {
  const [response, setResponse] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleResponse = async (responseType) => {
    if (!responseType) return;

    setLoading(true);
    setError(null);

    try {
      const result = await teamInvitationApi.respondToInvitation(
        invitation._id, 
        responseType, 
        message.trim()
      );

      if (result.success) {
        onResponse && onResponse(responseType, result);
        onClose();
      } else {
        setError(result.message || 'Failed to respond to invitation');
      }
    } catch (error) {
      setError('Error responding to invitation');
      console.error('Response error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getExpirationText = () => {
    if (!invitation.expiresAt) return null;
    
    const expiresAt = new Date(invitation.expiresAt);
    const now = new Date();
    const diffHours = Math.ceil((expiresAt - now) / (1000 * 60 * 60));
    
    if (diffHours <= 0) {
      return 'This invitation has expired';
    } else if (diffHours <= 24) {
      return `Expires in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    } else {
      const diffDays = Math.ceil(diffHours / 24);
      return `Expires in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }
  };

  const isExpired = () => {
    if (!invitation.expiresAt) return false;
    return new Date(invitation.expiresAt) <= new Date();
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h2>Team Invitation</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Invitation Content */}
        <div className={styles.content}>
          {/* Team Info */}
          <div className={styles.teamInfo}>
            <div className={styles.teamHeader}>
              <FaTrophy className={styles.teamIcon} />
              <div className={styles.teamDetails}>
                <h3>{invitation.teamName}</h3>
                <p>You've been invited to join this team</p>
              </div>
            </div>
          </div>

          {/* Role Info */}
          <div className={styles.roleInfo}>
            <div className={styles.roleHeader}>
              <FaUsers className={styles.roleIcon} />
              <div className={styles.roleDetails}>
                <h4>Assigned Role</h4>
                <span className={styles.roleName}>{invitation.roleName}</span>
              </div>
            </div>
            {invitation.roleDescription && (
              <p className={styles.roleDescription}>{invitation.roleDescription}</p>
            )}
          </div>

          {/* Invitation Message */}
          {invitation.message && (
            <div className={styles.invitationMessage}>
              <FaEnvelope className={styles.messageIcon} />
              <div className={styles.messageContent}>
                <h4>Personal Message</h4>
                <p>{invitation.message}</p>
              </div>
            </div>
          )}

          {/* Expiration Info */}
          {invitation.expiresAt && (
            <div className={`${styles.expirationInfo} ${isExpired() ? styles.expired : ''}`}>
              {isExpired() ? (
                <FaTimesCircle className={styles.expirationIcon} />
              ) : (
                <FaTimes className={styles.expirationIcon} />
              )}
              <span>{getExpirationText()}</span>
            </div>
          )}

          {/* Response Message */}
          <div className={styles.responseMessage}>
            <label htmlFor="responseMessage">Response Message (Optional)</label>
            <textarea
              id="responseMessage"
              placeholder="Add a message to your response..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              disabled={isExpired()}
            />
          </div>

          {error && <div className={styles.error}>{error}</div>}
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={onClose}>
            Close
          </button>
          
          {!isExpired() && (
            <>
              <button 
                className={styles.declineButton}
                onClick={() => handleResponse(INVITATION_RESPONSE.DECLINE)}
                disabled={loading}
              >
                <FaTimesCircle />
                Decline
              </button>
              <button 
                className={styles.acceptButton}
                onClick={() => handleResponse(INVITATION_RESPONSE.ACCEPT)}
                disabled={loading}
              >
                <FaCheck />
                Accept Invitation
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamInvitationResponseModal;
